<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue'
  import type { Sentence } from '@/types/Sentence.ts'
  import RichTextRenderer from '@/components/common/RichTextRenderer.vue'
  import type { AudioMetadata } from '@/types/Ibexa'
  import VolumeIcon from '@/components/icons/VolumeIcon.vue'
  import VgsButton from '@/components/common/VgsButton.vue'
  import VolumeSlashIcon from '@/components/icons/VolumeSlashIcon.vue'
  import { useFetchAudioFile } from '@/composables/useFetchAudioFile'
  import { usePlaySound } from '@/composables/usePlaySound'

  enum SentenceColor {
    'white-background',
    'yellow-background'
  }
  enum SentenceProfile {
    'Anna',
    'William',
    'Fred',
    'Elizabeth',
    'Vanessa',
    'Sophie',
    'Daniel',
    'Julian',
  }

  interface ProfilePictureMapping {
    [key: number]: string | undefined;
  }

  const profilePictureMapping: ProfilePictureMapping = {
    [SentenceProfile.Anna]: '/profiles/anna.webp',
    [SentenceProfile.William]: '/profiles/william.webp',
    [SentenceProfile.Fred]: '/profiles/fred.webp',
    [SentenceProfile.Elizabeth]: '/profiles/elizabeth.webp',
    [SentenceProfile.Vanessa]: '/profiles/vanessa.webp',
    [SentenceProfile.Sophie]: '/profiles/sophie.webp',
    [SentenceProfile.Daniel]: '/profiles/daniel.webp',
    [SentenceProfile.Julian]: '/profiles/julian.webp',
  }

  type Props = {
    element: Sentence
    index?: number
  }

  const props = withDefaults(defineProps<Props>(), {
    index: 0
  })

  const { fetchAudioFile } = useFetchAudioFile()
  const { isPlaying, audioElement, toggleSound } = usePlaySound()

  const audioFile = ref<AudioMetadata | undefined>(undefined)

  onMounted(async () => {
    audioFile.value = await fetchAudioFile(props.element.audioFile?.destinationContentId)
  })

  const color = computed(() => SentenceColor[props.element.color[0]])
  const profileName = computed(() => props.element.profileName ||  SentenceProfile[props.element.profile[0]])
  const position = computed(() => props.index % 2 === 0 ? 'left' : 'right')
  const audioUri = computed(() => audioFile.value?.metadata?.elementURI)
  const soundIcon = computed(() => isPlaying.value ? VolumeSlashIcon : VolumeIcon)
  const profileImageSource = profilePictureMapping[props.element.profile[0]]
</script>

<template>
  <div :class="['sentence-entry', position]">
    <img
      v-if="profileImageSource"
      :src="profileImageSource"
      :alt="profileName"
      class="profile"
    >

    <div :class="['sentence-and-name', position === 'right' ? 'profile-name-right' : '']">
      <span class="profile-name" v-text="profileName" />

      <div :class="['sentence', color]">
        <div>
          <VgsButton
            v-if="!!audioUri"
            :aria-label="isPlaying ? 'Pause lyd' : 'Spill av lyd'"
            shape="round"
            @click="toggleSound"
          >
            <audio ref="audioElement" :src="audioFile?.metadata?.elementURI" />
            <component :is="soundIcon" />
          </VgsButton>
        </div>
        <div>
          <RichTextRenderer :rich-text="element.sentence" />
          <div v-if="element.showTranslation" class="translation">
            <RichTextRenderer :rich-text="element.translation" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  $triangle-size: .8rem;

  .sentence-entry {
    display: flex;
    align-items: center;
    gap: 1.25rem;
    margin-bottom: 16px;
  }

  .profile {
    width: 47px;
    height: 47px;
    border-radius: 50%;
    background-color: var(--color-orange-20);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: .7rem;
  }

  .profile-name {
    color: var(--color-gray-40);
    font-size: .875rem;
  }

  .sentence-and-name {
    display: flex;
    flex-direction: column;
  }

  .profile-name-right {
    align-items: flex-end;
  }

  .sentence {
    display: flex;
    align-items: baseline;
    border: 2px solid var(--color-yellow-40);
    background-color: var(--color-white);
    padding: 1rem;
    position: relative;

    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      translate: -50% -50%;
      rotate: 45deg;
      width: $triangle-size;
      height: $triangle-size;
      border: 2px solid;
      background-color: var(--color-white);
    }

    &::before {
      left: 0;
      border-color: transparent transparent var(--color-yellow-40) var(--color-yellow-40);
    }

    &::after {
      left: 100%;
      border-color: var(--color-yellow-40) var(--color-yellow-40) transparent transparent;
    }
  }

  .white-background {
    background-color: var(--color-white);

    &.sentence::before, &.sentence::after {
      background-color: var(--color-white);
    }
  }

  .yellow-background {
    background-color: var(--color-yellow-20);

    &.sentence::before, &.sentence::after {
      background-color: var(--color-yellow-20);
    }
  }

  .left {
    .sentence::after {
      content: none;
    }

    .sentence-and-name:only-child {
      margin-left: calc($triangle-size / 2);
    }
  }

  .right {
    flex-direction: row-reverse;

    .sentence::before {
      content: none;
    }
  }

  :deep(.cms-p) {
    margin: 0;
  }

  .translation {
    :deep(.cms-p) {
      color: var(--color-gray-40);
    }
  }

</style>

